/**
 * VISTOERA_PLATFORM_ADMIN_CANVAS_NODE_CATALOG_ICON_ORDER_AVAILABILITY_
 * AND_VISIBILITY_FOUNDATION_V2
 * — Platform Admin "功能节点管理" page: Canvas Function Node Catalog rail
 * (icons / order / ONLINE|OFFLINE) + Provider Options for capable nodes.
 *
 * Keeps the existing Platform Admin design system (`SectionCard`,
 * `platformColors`, Ant Design Table / Switch / Modal / Popconfirm).
 */
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  DeleteOutlined,
  EditOutlined,
  HolderOutlined,
  EyeOutlined,
  PlusOutlined,
  ReloadOutlined,
  WarningFilled,
} from '@ant-design/icons'
import {
  Alert,
  Button,
  Empty,
  Form,
  Input,
  Modal,
  Popconfirm,
  Radio,
  Select,
  Space,
  Switch,
  Table,
  Tabs,
  Tag,
  Tooltip,
  Typography,
  message,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import type { KeyboardEvent as ReactKeyboardEvent, MouseEvent as ReactMouseEvent, ReactNode } from 'react'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearchParams } from 'react-router-dom'
import {
  platformDeleteNodeModelBinding,
  platformDeleteProviderOption,
  platformDisableProviderOption,
  platformEnableProviderOption,
  platformGetCapabilityOverview,
  platformGetModelBindingCandidates,
  platformListNodeCatalog,
  platformListNodeCatalogIcons,
  platformListNodeModelBindings,
  platformListProviderCapabilities,
  platformListProviderCredentials,
  platformListProviderOptionTargetProviders,
  platformListExecutionPlanWorkerRuntimes,
  type ExecutionPlanWorkerRuntimeRow,
  platformListProviderOptions,
  platformRemapProviderOption,
  platformRepairModelBinding,
  platformReorderNodeCatalog,
  platformReorderProviderOptions,
  platformSetDefaultProviderOption,
  platformSetNodeCatalogAvailability,
  platformSetNodeCatalogIcon,
  platformSetNodeModelBindingDefault,
  platformSetNodeModelBindingEnabled,
  platformSetNodeModelBindingSortOrder,
  platformSetProviderOptionWorkerOffline,
  platformSetProviderOptionWorkerOnline,
  platformUpdateProviderDefinition,
  platformUpdateProviderOptionDescription,
  platformUpdateProviderOptionDisplayName,
  type CapabilityOverview,
  type CanvasNodeCatalogEntry,
  type ModelBindingCandidate,
  type PlatformCustomIconRow,
  type ProviderCapableNodeRow,
  type ProviderOptionAdminRow,
  type ProviderOptionMappingInput,
} from '../api'
import type { NodeModelBindingRow } from '../types'
import AddModelDrawer from './AddModelDrawer'
import AddProviderDrawer from './AddProviderDrawer'
import ExecutionPlanDetailDrawer from './ExecutionPlanDetailDrawer'
import NodeIconPicker from './NodeIconPicker'
import NodeQuickActionsPanel from './NodeQuickActionsPanel'
import NodeSkillsPanel from './NodeSkillsPanel'
import { getNodeDefinition, resolveIconByKey, setNodeIconOverrides } from '../workspace/contentProduction/canvas/nodeRegistry'
import { SectionCard, WorkerStatusBadge } from './adminShared'
import {
  EXECUTION_PLAN_CREATE_TARGET_TYPES,
  WORKER_DISPATCH_AUTO,
  WORKER_DISPATCH_PINNED,
  formatWorkerRuntimeOptionLabel,
  isExternalApiExecutionTarget,
  isWorkerExecutionTarget,
} from './executionPlanMappingUi'
import { shouldShowWorkerHeartbeatBadge } from './providerOptionCanonicalStatus'
import { normalizeExecutionPlanSummary } from './providersExecutionPlanSummary'
import { sortProviderOptionsForAdmin } from './providerOptionAdminSort'
import {
  PROVIDER_NODE_QUERY_KEY,
  PROVIDER_NODE_STORAGE_KEY,
  resolveProviderNodeType,
} from './providerNodeSelection'
import { platformColors } from './theme'
import './providerOptionsTable.css'

/** Desktop table min width: fixed columns + large description flex column.
 * Prefer horizontal scroll over crushing description into character wrap. */
const COL_WIDTH = {
  displayName: 180,
  description: 380,
  // VISTOERA_PROVIDER_MODEL_EXECUTION_OPTION_CANONICAL_SEMANTICS_AND_
  // FASHN_CATALOG_RECONCILIATION_P0_V1 §8 — the old single mixed "实际
  // 厂商 / Worker" `vendor` column is now three narrower, single-concept
  // columns (`provider`/`model`/`executionTarget`); kept the same total
  // footprint (roughly 150 -> 110+110+110) rather than growing the table.
  provider: 110,
  model: 110,
  executionTarget: 110,
  type: 100,
  status: 110,
  enabled: 72,
  default: 100,
  usage: 110,
  lifecycle: 100,
  actions: 148,
} as const

const PROVIDER_OPTIONS_TABLE_MIN_WIDTH =
  COL_WIDTH.displayName +
  COL_WIDTH.description +
  COL_WIDTH.provider +
  COL_WIDTH.model +
  COL_WIDTH.executionTarget +
  COL_WIDTH.type +
  COL_WIDTH.status +
  COL_WIDTH.enabled +
  COL_WIDTH.default +
  COL_WIDTH.usage +
  COL_WIDTH.lifecycle +
  COL_WIDTH.actions

type TargetWorker = { id: string; name: string; status: string; online: boolean }
type TargetProvider = {
  id: string
  provider_key: string
  display_name: string
  provider_type: string
  credential_required: boolean
  // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_P0_V1
  // — server-resolved execution adapter (spec §7/§9).
  execution_target_type?: string
  auto_resolved?: boolean
  adapter_key?: string | null
  adapter_display_label?: string | null
  selectable_adapters?: Array<{ key: string; display_name: string }>
}
type CredentialOption = {
  id: string
  name: string
  provider_key: string
  status: string
  health_status: string | null
  environment: string
}

function toCredentialOptions(
  rows: Array<{
    id: string
    name: string
    provider_key: string | null
    status: string
    health_status: string | null
    environment: string
  }>,
): CredentialOption[] {
  return rows.map((r) => ({
    id: r.id,
    name: r.name,
    provider_key: r.provider_key || '',
    status: r.status,
    health_status: r.health_status,
    environment: r.environment,
  }))
}

const TARGET_TYPE_LABEL: Record<string, string> = {
  LOCAL_WORKER: 'Worker',
  EXTERNAL_API: '外部 API',
  BROWSER_PROVIDER: 'Worker',
  FUTURE: '预留',
}

const TARGET_TYPE_TAG_COLOR: Record<string, string> = {
  LOCAL_WORKER: 'cyan',
  EXTERNAL_API: 'blue',
  BROWSER_PROVIDER: 'purple',
  FUTURE: 'default',
}

function errMsg(err: unknown, fallback: string): string {
  const e = err as {
    response?: {
      data?: {
        detail?:
          | {
              message?: string
              code?: string
              occupied_by_display_name?: string
              occupied_by_node_type?: string
            }
          | string
      }
    }
    message?: string
  }
  const detail = e?.response?.data?.detail
  if (detail && typeof detail === 'object') {
    if (detail.code === 'ICON_ALREADY_ASSIGNED') {
      const owner = detail.occupied_by_display_name || detail.occupied_by_node_type || '其他节点'
      return `该图标已被「${owner}」使用，请选择其他图标。`
    }
    if (detail.message) return detail.message
  }
  if (typeof detail === 'string') return detail
  return e?.message || fallback
}

function catalogEntryIcon(
  entry: CanvasNodeCatalogEntry,
  iconOverrides: Record<string, string>,
  customIconsByRef: Record<string, PlatformCustomIconRow>,
): ReactNode {
  const ref = iconOverrides[entry.node_type] || entry.icon_ref
  if (ref?.startsWith('custom:')) {
    const custom = customIconsByRef[ref]
    if (custom?.url) {
      return <img src={custom.url} alt="" style={{ width: 22, height: 22, objectFit: 'contain' }} />
    }
    return <span style={{ fontSize: 14, opacity: 0.45 }}>◇</span>
  }
  return resolveIconByKey(ref)
}

function catalogEntryColor(entry: CanvasNodeCatalogEntry): string {
  return getNodeDefinition(entry.node_type).color
}

function nodeMeta(node: ProviderCapableNodeRow, iconOverrides: Record<string, string> = {}) {
  const iconTarget = node.icon_target || node.canvas_node_type || `capability:${node.capability}`
  const overrideKey = node.icon_key || iconOverrides[iconTarget] || null
  if (node.canvas_node_type) {
    const def = getNodeDefinition(node.canvas_node_type)
    const icon = overrideKey ? resolveIconByKey(overrideKey) : def.icon
    return {
      icon,
      iconKey: (overrideKey || def.iconKey) as string | undefined,
      color: def.color,
      labelZh: def.label || node.fallback_label_zh,
      labelEn: node.fallback_label_en,
      description: def.description || '',
      iconTarget,
    }
  }
  return {
    icon: overrideKey ? resolveIconByKey(overrideKey) : getNodeDefinition('__missing__').icon,
    iconKey: (overrideKey || undefined) as string | undefined,
    color: '#94a3b8',
    labelZh: node.fallback_label_zh,
    labelEn: node.fallback_label_en,
    description: '',
    iconTarget,
  }
}

/** Task spec §25 inline edit UX — click text → input, Enter/blur save,
 * Esc cancel; failed save restores the previous value (no silent data
 * loss) instead of leaving a possibly-unsaved draft on screen. Task
 * spec §26 performance — exactly ONE PATCH per confirmed edit, never
 * per keystroke. */
function InlineEditText({
  value,
  placeholder,
  multiline,
  disabled,
  onSave,
  textClassName,
}: {
  value: string
  placeholder?: string
  multiline?: boolean
  disabled?: boolean
  onSave: (next: string) => Promise<void>
  textClassName?: string
}) {
  const [editing, setEditing] = useState(false)
  const [draft, setDraft] = useState(value)
  const [saving, setSaving] = useState(false)

  useEffect(() => {
    if (!editing) setDraft(value)
  }, [value, editing])

  const commit = async () => {
    const next = draft.trim()
    if (next === value.trim()) {
      setEditing(false)
      return
    }
    setSaving(true)
    try {
      await onSave(next)
      setEditing(false)
    } catch {
      setDraft(value)
    } finally {
      setSaving(false)
    }
  }

  const cancel = () => {
    setDraft(value)
    setEditing(false)
  }

  if (!editing) {
    const body = (
      <span
        role={disabled ? undefined : 'button'}
        tabIndex={disabled ? undefined : 0}
        onClick={() => !disabled && setEditing(true)}
        onKeyDown={(e) => {
          if (disabled) return
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault()
            setEditing(true)
          }
        }}
        className="provider-option-inline-edit"
        style={{ cursor: disabled ? 'default' : 'text' }}
      >
        <span className={textClassName}>
          {value || <span style={{ color: platformColors.mutedText }}>{placeholder || '—'}</span>}
        </span>
        {!disabled && <EditOutlined style={{ fontSize: 11, opacity: 0.35, flexShrink: 0, marginTop: 3 }} />}
      </span>
    )
    return value ? (
      <Tooltip title={value} placement="topLeft">
        {body}
      </Tooltip>
    ) : (
      body
    )
  }

  return multiline ? (
    <Input.TextArea
      autoFocus
      size="small"
      rows={2}
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e: ReactKeyboardEvent<HTMLTextAreaElement>) => {
        if (e.key === 'Escape') cancel()
        if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) {
          e.preventDefault()
          void commit()
        }
      }}
    />
  ) : (
    <Input
      autoFocus
      size="small"
      value={draft}
      disabled={saving}
      onChange={(e) => setDraft(e.target.value)}
      onBlur={() => void commit()}
      onKeyDown={(e: ReactKeyboardEvent<HTMLInputElement>) => {
        if (e.key === 'Escape') cancel()
        if (e.key === 'Enter') {
          e.preventDefault()
          void commit()
        }
      }}
    />
  )
}

/**
 * VISTOERA_PROVIDER_MODEL_EXECUTION_OPTION_CANONICAL_SEMANTICS_AND_FASHN_
 * CATALOG_RECONCILIATION_P0_V1 (task spec §8 hard rule) — this table used
 * to render one mixed "实际厂商 / Worker" column (`vendorLabel`, kept below
 * as `executionTargetLabel`) that conflated Provider identity with
 * execution-target identity. Split into three honest, single-concept
 * columns instead:
 *   - `providerLabel` — real `ProviderDefinition` identity only (EXTERNAL_
 *     API/BROWSER_PROVIDER); a LOCAL_WORKER mapping genuinely has no
 *     Provider FK today (task spec §5 forensic finding — "Vistoera 换装"
 *     etc.), so this honestly renders "—" rather than fabricating one.
 *   - `modelLabel` — canonical `ProviderModel` identity (task spec §5/§6),
 *     sourced from the new `mapping.model_display_name` traceability
 *     field; "—" is a legitimate not-yet-backfilled/model-less state.
 *   - `executionTargetLabel` — "where does this actually run" (Worker
 *     name for LOCAL_WORKER/BROWSER_PROVIDER, adapter_key for EXTERNAL_
 *     API) — the literal "执行目标" column task spec §8 explicitly allows
 *     alongside Provider/Model.
 */
/** VistoeraTASK-00337 — Provider column: Worker Registry name (Worker-backed) or API provider definition. */
function providerLabel(row: ProviderOptionAdminRow): string {
  const fromApi = row.admin_provider_display_name?.trim()
  if (fromApi) return fromApi
  if (row.worker_backed_scheme && row.resolved_worker_display_name?.trim()) {
    return row.resolved_worker_display_name.trim()
  }
  const m = row.mapping
  if (!m) return '—'
  if (m.execution_target_type === 'EXTERNAL_API') {
    return m.provider_display_name || m.provider_key || '—'
  }
  return '—'
}

function modelLabel(row: ProviderOptionAdminRow): string {
  // TASK-00183 — needs_model_repair (genuinely broken FIXED_MODEL) and
  // RUNTIME_SELECTION_REQUIRED (a legitimate pooled multi-model Option
  // awaiting an explicit default choice) are DISTINCT states — never
  // collapsed back into one red "需要修复" label (task spec §9 hard
  // rule: RUNTIME_SELECTION_REQUIRED is not damage).
  if (row.mapping?.needs_model_repair) {
    return '需要修复模型绑定'
  }
  if (row.mapping?.model_binding_status === 'RUNTIME_SELECTION_REQUIRED') {
    return '待选择默认模型'
  }
  return row.mapping?.model_display_name || '—'
}

function executionTargetLabel(row: ProviderOptionAdminRow): string {
  const m = row.mapping
  if (!m) return '—'
  if (m.execution_target_type === 'LOCAL_WORKER') {
    // VISTOERA_FUNCTION_NODE_STANDARD_CREATIVE_UNBOUND_WORKER_MAPPING_
    // ROOT_CAUSE_AND_RUNTIME_RESOLUTION_P0_V2 (item 五) — a bare "—" for
    // a LOCAL_WORKER mapping with no worker_id is indistinguishable from
    // "we don't know" (e.g. a loading/error state); render the same
    // explicit "未绑定"/"绑定失效" words the runtime_status Tag itself
    // uses so this column and the 运行状态 column always tell the same
    // story, never a silent dash next to a red 离线 badge again.
    if (m.worker_name) return m.worker_name
    if (m.worker_id) return m.runtime_status?.code === 'stale_binding' ? `${m.worker_id}（绑定失效）` : m.worker_id
    return '未绑定'
  }
  if (m.execution_target_type === 'BROWSER_PROVIDER') {
    if (m.worker_name) return m.worker_name
    if (m.worker_id) return m.runtime_status?.code === 'stale_binding' ? `${m.worker_id}（绑定失效）` : m.worker_id
    return m.provider_display_name || m.provider_key || '未绑定'
  }
  return m.adapter_key || '—'
}

/** VISTOERA_PLATFORM_WORKER_STICKY_HEADER_CAPABILITY_MATRIX_VISIBILITY_
 * AND_PROVIDER_RUNTIME_STATE_UNIFICATION_P0_V1 (item 十四/二十九) — one
 * canonical tone->antd-Tag-color map so every execution_target_type's
 * "运行状态" cell (LOCAL_WORKER/BROWSER_PROVIDER/EXTERNAL_API) shares the
 * same visual vocabulary, never a per-type bespoke palette. */
const RUNTIME_TONE_COLOR: Record<string, string> = {
  success: 'green',
  info: 'blue',
  warning: 'orange',
  danger: 'red',
  neutral: 'default',
}

function suggestedWorkerHint(m: ProviderOptionAdminRow['mapping']): string | undefined {
  if (!m?.suggested_worker) return undefined
  const extra = m.suggested_worker.candidate_count > 1 ? `，共 ${m.suggested_worker.candidate_count} 个可选` : ''
  return `当前绑定的 Worker 不可用；检测到在线 Worker「${m.suggested_worker.worker_name}」已声明相同能力，可考虑“重映射”到它${extra}。`
}

function statusCell(row: ProviderOptionAdminRow) {
  const m = row.mapping
  if (!m) return <Tag>未配置</Tag>
  if (m.execution_target_type === 'LOCAL_WORKER') {
    // VISTOERA_FUNCTION_NODE_STANDARD_CREATIVE_UNBOUND_WORKER_MAPPING_
    // ROOT_CAUSE_AND_RUNTIME_RESOLUTION_P0_V2 (item 五/CASE_3/CASE_4)
    // forensic finding: this cell used to feed `worker_display_status`
    // (only ever populated when `worker_id` resolves to a REAL Worker
    // row) straight into `WorkerStatusBadge`, whose own `displayStatus
    // ?? (online ? 'online' : 'offline')` fallback silently rendered
    // "离线"/OFFLINE (danger/red) for a mapping that was never bound to
    // any Worker at all (`worker_id === null`) — indistinguishable from
    // "bound to a real, currently-unreachable machine". A missing/never
    // -bound Worker is now rendered from the canonical `runtime_status`
    // object (already computed by `_mapping_availability` — "unbound"/
    // "stale_binding"/"worker_disabled"/"worker_draining" all have their
    // own honest label+tone there); `WorkerStatusBadge` is used ONLY for
    // the genuine "a real Worker row was resolved" case, so its still-
    // useful online/busy/draining nuance survives for a HEALTHY binding.
    const hint = suggestedWorkerHint(m)
    const rs = m.runtime_status
    // VistoeraTASK-00364 — the "运行状态" cell must be the canonical
    // execution-option availability (`runtime_status.available`), never a
    // raw Worker heartbeat badge just because a real Worker happens to be
    // resolved+online. Before this fix, ANY mapping with `worker_id` +
    // `worker_display_status` set fell straight into `WorkerStatusBadge`
    // below regardless of `runtime_status` — so a Worker that is online
    // but whose mapping has no AI Harness execution route (DeepSeek/Grok
    // text: `runtime_status.code === 'adapter_missing'`, `available:
    // false`) still rendered a plain green "在线" badge, the exact
    // ADMIN_DEEPSEEK/GROK=ONLINE+ENABLED vs CANVAS=UNAVAILABLE drift this
    // task fixes. `WorkerStatusBadge`'s online/busy/draining nuance is
    // shown ONLY once the canonical resolver already agrees the option is
    // selectable; any other case defers to `runtime_status`'s own honest
    // label+tone (unbound/stale_binding/worker_disabled/worker_draining/
    // worker_offline/capability_mismatch/adapter_missing/... — all already
    // computed by `_mapping_availability`, never re-derived here).
    if (!shouldShowWorkerHeartbeatBadge(m)) {
      return (
        <Tooltip title={hint}>
          <Tag color={RUNTIME_TONE_COLOR[rs?.tone || 'neutral']}>{rs?.label || '未绑定'}</Tag>
        </Tooltip>
      )
    }
    return (
      <Tooltip title={hint}>
        <span>
          <WorkerStatusBadge displayStatus={m.worker_display_status} online={m.worker_online ?? false} />
        </span>
      </Tooltip>
    )
  }
  if (m.execution_target_type === 'EXTERNAL_API') {
    // Item 六/八/二十六 — a globally-disabled Provider (Provider Registry
    // "Operational Enabled" toggle, `ProviderDefinition.status`) must be
    // visually obvious and take priority over credential health, since
    // it means "no new execution regardless of credential validity".
    if (m.provider_operational_status === 'disabled') {
      return (
        <Tooltip title="该 Provider 已在 Provider 注册表全局停用：Credential/绑定均保留，重新启用后自动恢复路由。">
          <Tag color="default">已停用</Tag>
        </Tooltip>
      )
    }
    // VISTOERA_DEEPSEEK_FUNCTION_NODE_PROVIDER_EXECUTION_ADAPTER_
    // RESOLUTION_AND_EXECUTABILITY_STATUS_CONSISTENCY_P0_V1 — primary
    // "运行状态" is canonical runtime_status (EXECUTION_READY), not
    // credential health alone. Credential detail stays in tooltip.
    const rs = m.runtime_status
    const cs = m.credential_status
    const health = (cs?.health || m.credential_health_status || '').toUpperCase()
    const name = cs?.credential_name || m.credential_name
    const source = cs?.source
    const credentialDetail =
      cs?.resolved && name
        ? `凭证：${name}${source === 'platform_default' ? '（平台默认）' : source === 'explicit' ? '（显式绑定）' : ''}${health ? ` · ${health}` : ''}`
        : cs?.reason_code
          ? `凭证未解析：${cs.reason_code}`
          : !m.credential_id
            ? '凭证未绑定'
            : health
              ? `凭证：${health}`
              : undefined
    const tip = [rs?.label ? `执行：${rs.label}` : undefined, credentialDetail].filter(Boolean).join(' · ')

    if (rs?.label) {
      return (
        <Tooltip title={tip || undefined}>
          <Tag color={RUNTIME_TONE_COLOR[rs.tone || 'neutral']}>{rs.label}</Tag>
        </Tooltip>
      )
    }

    // Legacy fallback when runtime_status is absent (stale FE/BE mix).
    let tag: ReactNode
    if (cs?.resolved && cs.enabled) {
      if (health === 'HEALTHY' || health === 'VALID' || health === 'OK') {
        tag = <Tag color="green">{source === 'platform_default' ? '默认凭证' : '凭证正常'}</Tag>
      } else if (health === 'INVALID') {
        tag = <Tag color="red">凭证无效</Tag>
      } else {
        tag = <Tag color="orange">凭证 {health || '已绑定'}</Tag>
      }
    } else {
      tag = <Tag color="orange">凭证未绑定</Tag>
    }
    return tip ? <Tooltip title={tip}>{tag}</Tooltip> : tag
  }
  if (m.execution_target_type === 'BROWSER_PROVIDER') {
    // Item 五 — Browser Provider must derive real runtime status
    // (provider identity + capability + exact Worker), never a
    // hardcoded generic "浏览器 Provider" label regardless of whether
    // it's actually online.
    if (m.provider_operational_status === 'disabled') {
      return (
        <Tooltip title="该 Provider 已在 Provider 注册表全局停用。">
          <Tag color="default">已停用</Tag>
        </Tooltip>
      )
    }
    const rs = m.runtime_status
    const hint = suggestedWorkerHint(m)
    return (
      <Tooltip title={hint}>
        <Tag color={RUNTIME_TONE_COLOR[rs?.tone || 'neutral']}>{rs?.label || '浏览器 Provider'}</Tag>
      </Tooltip>
    )
  }
  return <Tag>预留</Tag>
}

export default function ProvidersPage() {
  const { t } = useTranslation('platformAdmin')
  const [searchParams, setSearchParams] = useSearchParams()
  const [catalog, setCatalog] = useState<CanvasNodeCatalogEntry[]>([])
  const [capabilityByKey, setCapabilityByKey] = useState<Record<string, ProviderCapableNodeRow>>({})
  const [nodesLoading, setNodesLoading] = useState(true)
  const [nodesError, setNodesError] = useState<string | null>(null)
  const [selectedNodeType, setSelectedNodeType] = useState<string | null>(null)
  const [customIconsByRef, setCustomIconsByRef] = useState<Record<string, PlatformCustomIconRow>>({})
  const [availabilityBusy, setAvailabilityBusy] = useState(false)

  const [options, setOptions] = useState<ProviderOptionAdminRow[]>([])
  const [executionPlanSummary, setExecutionPlanSummary] = useState<{
    total: number
    worker_backed: number
    api_backed: number
    executable: number
    default_display_name: string | null
  } | null>(null)
  const [optionsLoading, setOptionsLoading] = useState(false)
  const [optionsError, setOptionsError] = useState<string | null>(null)

  // VISTOERA_CANONICAL_PROVIDER_MODEL_CATALOG_NODE_MODEL_BINDING_USAGE_
  // BILLING_FOUNDATION_P0_V1 (task spec §11/§31) — NodeModelBinding admin
  // panel, additive alongside the legacy Provider Option table above
  // (never replacing it this round — task spec §15/§28: GenerateVideo is
  // the first full landing sample, other capabilities keep working
  // unmodified via the existing Provider Option flow until migrated).
  const [nodeModelBindings, setNodeModelBindings] = useState<NodeModelBindingRow[]>([])
  const [nodeModelBindingsLoading, setNodeModelBindingsLoading] = useState(false)
  const [nodeModelBindingBusyId, setNodeModelBindingBusyId] = useState<string | null>(null)
  const [addModelOpen, setAddModelOpen] = useState(false)
  const [overview, setOverview] = useState<CapabilityOverview | null>(null)
  const [overviewLoading, setOverviewLoading] = useState(false)
  const [activeTab, setActiveTab] = useState<string>('overview')

  const [searchText, setSearchText] = useState('')
  const [enabledFilter, setEnabledFilter] = useState<boolean | undefined>(undefined)
  const [targetFilter, setTargetFilter] = useState<string | undefined>(undefined)

  const [busyId, setBusyId] = useState<string | null>(null)

  // VISTOERA_CANONICAL_NODE_REGISTRY... spec §9/§11 — Node Icon
  // override map + approved catalog, fetched once per page mount (not
  // per node) and pushed into the shared `nodeRegistry` cache so every
  // Canvas surface reading `getNodeDefinition()` sees the same value
  // (spec §12 ICON_FETCH_PER_NODE=0).
  const [iconOverrides, setIconOverridesState] = useState<Record<string, string>>({})
  const [iconPickerOpen, setIconPickerOpen] = useState(false)
  const [iconSaving, setIconSaving] = useState(false)

  const [createOpen, setCreateOpen] = useState(false)

  const [remapOpen, setRemapOpen] = useState(false)
  const [remapRow, setRemapRow] = useState<ProviderOptionAdminRow | null>(null)
  const [detailOptionId, setDetailOptionId] = useState<string | null>(null)
  const [detailOpen, setDetailOpen] = useState(false)
  const [remapForm] = Form.useForm()
  const [remapTargetType, setRemapTargetType] = useState<string>('LOCAL_WORKER')
  const [remapping, setRemapping] = useState(false)
  // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_P0_V1
  // (spec §9/§15) — the Remap modal must apply the exact same
  // server-resolved-adapter treatment as the "+新增 Provider" drawer;
  // never a second, freeform Adapter Key text input.
  const remapProviderId = Form.useWatch('provider_definition_id', remapForm) as string | undefined
  const remapWorkerDispatchMode = Form.useWatch('worker_dispatch_mode', remapForm) as string | undefined

  // TASK-00183 — canonical Model Binding repair Dialog (distinct from
  // the Remap modal above: never touches worker_id/provider_definition_
  // id/credential_id/adapter_key/config). Candidates always come from
  // the backend canonical resolver (`platformGetModelBindingCandidates`)
  // — never assembled client-side (task spec §11).
  const [modelBindingOpen, setModelBindingOpen] = useState(false)
  const [modelBindingRow, setModelBindingRow] = useState<ProviderOptionAdminRow | null>(null)
  const [modelBindingLoading, setModelBindingLoading] = useState(false)
  const [modelBindingSubmitting, setModelBindingSubmitting] = useState(false)
  const [modelBindingCandidates, setModelBindingCandidates] = useState<ModelBindingCandidate[]>([])
  const [modelBindingSelected, setModelBindingSelected] = useState<string | null>(null)
  const [modelBindingSafeMessage, setModelBindingSafeMessage] = useState<string | null>(null)

  const [targetWorkers, setTargetWorkers] = useState<TargetWorker[]>([])
  const [workerRuntimes, setWorkerRuntimes] = useState<ExecutionPlanWorkerRuntimeRow[]>([])
  const [workerRuntimesError, setWorkerRuntimesError] = useState<string | null>(null)
  const [targetProviders, setTargetProviders] = useState<TargetProvider[]>([])
  const [credentials, setCredentials] = useState<CredentialOption[]>([])

  // Catalog rail drag-reorder (same mouse-drag pattern as LeftLibraryPanel)
  const [dragFromIndex, setDragFromIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const dragOverRef = useRef<number | null>(null)
  const catalogRef = useRef(catalog)
  useEffect(() => {
    catalogRef.current = catalog
  }, [catalog])

  const selectedEntry = useMemo(
    () => catalog.find((e) => e.node_type === selectedNodeType) ?? null,
    [catalog, selectedNodeType],
  )
  const selectedCapability = selectedEntry?.capability || null
  const supportsProvider = Boolean(
    selectedEntry && (selectedEntry.supports_provider || selectedEntry.capability),
  )
  const selectedCapabilityRow = selectedCapability ? capabilityByKey[selectedCapability] ?? null : null
  const selectedMeta = selectedCapabilityRow ? nodeMeta(selectedCapabilityRow, iconOverrides) : null
  const selectedColor = selectedEntry ? catalogEntryColor(selectedEntry) : '#94a3b8'
  const selectedIcon = selectedEntry
    ? catalogEntryIcon(selectedEntry, iconOverrides, customIconsByRef)
    : null
  const selectedIconKey =
    (selectedNodeType && iconOverrides[selectedNodeType]) || selectedEntry?.icon_ref || undefined

  const loadCustomIcons = useCallback(async () => {
    try {
      const res = await platformListNodeCatalogIcons()
      const map: Record<string, PlatformCustomIconRow> = {}
      for (const row of res.data.icons ?? []) map[row.icon_ref] = row
      setCustomIconsByRef(map)
    } catch {
      setCustomIconsByRef({})
    }
  }, [])

  const selectNodeType = useCallback(
    (nodeType: string) => {
      setSelectedNodeType(nodeType)
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev)
          next.set(PROVIDER_NODE_QUERY_KEY, nodeType)
          return next
        },
        { replace: true },
      )
      try {
        window.localStorage.setItem(PROVIDER_NODE_STORAGE_KEY, nodeType)
      } catch {
        /* ignore */
      }
    },
    [setSearchParams],
  )

  const loadNodes = useCallback(async (opts?: { keepSelection?: boolean }) => {
    setNodesLoading(true)
    setNodesError(null)
    try {
      const [catalogRes, capsRes] = await Promise.all([
        platformListNodeCatalog(),
        platformListProviderCapabilities().catch(() => ({ data: { nodes: [] as ProviderCapableNodeRow[] } })),
      ])
      const list = catalogRes.data.entries ?? []
      setCatalog(list)
      // Catalog icon_ref is occupancy + assignment SoT for Admin surfaces.
      const iconMap: Record<string, string> = {}
      for (const e of list) {
        if (e.icon_ref) iconMap[e.node_type] = e.icon_ref
      }
      setNodeIconOverrides(iconMap)
      setIconOverridesState(iconMap)
      const capMap: Record<string, ProviderCapableNodeRow> = {}
      for (const n of capsRes.data.nodes ?? []) capMap[n.capability] = n
      setCapabilityByKey(capMap)
      let storedNode: string | null = null
      try {
        storedNode = window.localStorage.getItem(PROVIDER_NODE_STORAGE_KEY)
      } catch {
        storedNode = null
      }
      setSelectedNodeType((prev) =>
        resolveProviderNodeType(list, {
          keepSelection: opts?.keepSelection,
          previous: prev,
          urlNode: searchParams.get(PROVIDER_NODE_QUERY_KEY),
          storedNode,
        }),
      )
    } catch (err) {
      setNodesError(errMsg(err, '加载 Node Catalog 失败'))
    } finally {
      setNodesLoading(false)
    }
  }, [searchParams])

  useEffect(() => {
    if (!selectedNodeType) return
    const el = document.querySelector(`[data-testid="provider-node-rail-item-${selectedNodeType}"]`)
    el?.scrollIntoView({ block: 'nearest' })
  }, [selectedNodeType, catalog.length])

  const loadOptions = useCallback(async (capability: string) => {
    setOptionsLoading(true)
    setOptionsError(null)
    try {
      const res = await platformListProviderOptions(capability)
      setOptions(res.data.options ?? [])
      setExecutionPlanSummary(normalizeExecutionPlanSummary(res.data.execution_plan_summary))
    } catch (err) {
      setOptionsError(errMsg(err, '加载执行方案失败'))
      setOptions([])
    } finally {
      setOptionsLoading(false)
    }
  }, [])

  const loadNodeModelBindings = useCallback(async (capability: string) => {
    setNodeModelBindingsLoading(true)
    try {
      const res = await platformListNodeModelBindings(capability)
      setNodeModelBindings(res.data.items ?? [])
    } catch {
      setNodeModelBindings([])
    } finally {
      setNodeModelBindingsLoading(false)
    }
  }, [])

  const loadOverview = useCallback(async (capability: string) => {
    setOverviewLoading(true)
    try {
      const res = await platformGetCapabilityOverview(capability)
      setOverview(res.data)
    } catch {
      setOverview(null)
    } finally {
      setOverviewLoading(false)
    }
  }, [])

  const loadTargetPickers = useCallback(async (capability: string) => {
    try {
      const [runtimesRes, providersRes] = await Promise.all([
        platformListExecutionPlanWorkerRuntimes(),
        platformListProviderOptionTargetProviders(capability),
      ])
      const runtimes = runtimesRes.data.workers ?? []
      setWorkerRuntimes(runtimes)
      setWorkerRuntimesError(null)
      setTargetWorkers(
        runtimes.map((w) => ({
          id: w.worker_id,
          name: w.display_name,
          status: w.status,
          online: w.online,
        })),
      )
      setTargetProviders(providersRes.data.providers ?? [])
    } catch {
      setWorkerRuntimes([])
      setWorkerRuntimesError('Worker 列表暂不可用')
      setTargetWorkers([])
      setTargetProviders([])
    }
  }, [])

  useEffect(() => {
    void loadNodes()
    void loadCustomIcons()
  }, [loadNodes, loadCustomIcons])

  // Do NOT load legacy provider-options/node-icons as occupancy SoT —
  // Platform Node Catalog entries (including OFFLINE) own icon_ref.
  const onPickIcon = async (iconKey: string) => {
    if (!selectedNodeType) {
      message.error('当前功能节点无法修改图标')
      return
    }
    setIconSaving(true)
    try {
      await platformSetNodeCatalogIcon(selectedNodeType, iconKey)
      const next = { ...iconOverrides, [selectedNodeType]: iconKey }
      setNodeIconOverrides(next)
      setIconOverridesState(next)
      setCatalog((prev) =>
        prev.map((e) => (e.node_type === selectedNodeType ? { ...e, icon_ref: iconKey } : e)),
      )
      if (iconKey.startsWith('custom:')) await loadCustomIcons()
      message.success('图标已更新；租户 Canvas 刷新后左栏 / Node Header / Hover Quick Action 同步')
      setIconPickerOpen(false)
    } catch (err) {
      message.error(errMsg(err, '更新图标失败'))
    } finally {
      setIconSaving(false)
    }
  }

  const onToggleAvailability = async (online: boolean) => {
    if (!selectedNodeType) return
    setAvailabilityBusy(true)
    try {
      const res = await platformSetNodeCatalogAvailability(selectedNodeType, online ? 'ONLINE' : 'OFFLINE')
      setCatalog((prev) => prev.map((e) => (e.node_type === selectedNodeType ? { ...e, ...res.data } : e)))
      message.success(online ? '已上线' : '已下线')
    } catch (err) {
      message.error(errMsg(err, '更新可用性失败'))
    } finally {
      setAvailabilityBusy(false)
    }
  }

  const startCatalogReorder = useCallback((e: ReactMouseEvent, index: number) => {
    e.preventDefault()
    e.stopPropagation()
    setDragFromIndex(index)
    setDragOverIndex(index)
    dragOverRef.current = index
  }, [])

  useEffect(() => {
    if (dragFromIndex === null) return
    const onMove = (e: MouseEvent) => {
      const el = document.elementFromPoint(e.clientX, e.clientY) as HTMLElement | null
      const wrap = el?.closest('[data-catalog-index]') as HTMLElement | null
      if (!wrap) return
      const idx = Number(wrap.dataset.catalogIndex)
      if (!Number.isNaN(idx) && idx !== dragOverRef.current) {
        dragOverRef.current = idx
        setDragOverIndex(idx)
      }
    }
    const onUp = () => {
      const from = dragFromIndex
      const to = dragOverRef.current
      void (async () => {
        if (from !== null && to !== null && to !== from) {
          const current = catalogRef.current
          const next = [...current]
          const [item] = next.splice(from, 1)
          const insertAt = to > from ? to - 1 : to
          next.splice(insertAt, 0, item)
          const orderedTypes = next.map((e) => e.node_type)
          setCatalog(next)
          try {
            const res = await platformReorderNodeCatalog(orderedTypes)
            setCatalog(res.data.entries ?? next)
            message.success('节点顺序已更新')
          } catch (err) {
            message.error(errMsg(err, '调整顺序失败'))
            await loadNodes({ keepSelection: true })
          }
        }
        setDragFromIndex(null)
        setDragOverIndex(null)
        dragOverRef.current = null
      })()
    }
    window.addEventListener('mousemove', onMove)
    window.addEventListener('mouseup', onUp)
    return () => {
      window.removeEventListener('mousemove', onMove)
      window.removeEventListener('mouseup', onUp)
    }
  }, [dragFromIndex, loadNodes])

  useEffect(() => {
    if (!selectedCapability || !supportsProvider) {
      setOptions([])
      setOptionsError(null)
      setOverview(null)
      setNodeModelBindings([])
      return
    }
    void loadOptions(selectedCapability)
    void loadTargetPickers(selectedCapability)
    void loadOverview(selectedCapability)
    void loadNodeModelBindings(selectedCapability)
  }, [selectedCapability, supportsProvider, loadOptions, loadTargetPickers, loadOverview, loadNodeModelBindings])

  const filteredOptions = useMemo(() => {
    const needle = searchText.trim().toLowerCase()
    const filtered = options.filter((row) => {
      if (enabledFilter !== undefined && row.enabled !== enabledFilter) return false
      if (targetFilter && row.mapping?.execution_target_type !== targetFilter) return false
      if (
        needle &&
        !row.frontend_display_name.toLowerCase().includes(needle) &&
        !(row.admin_list_display_name || '').toLowerCase().includes(needle) &&
        !(row.frontend_description || '').toLowerCase().includes(needle) &&
        !providerLabel(row).toLowerCase().includes(needle) &&
        !modelLabel(row).toLowerCase().includes(needle) &&
        !executionTargetLabel(row).toLowerCase().includes(needle)
      ) {
        return false
      }
      return true
    })
    return sortProviderOptionsForAdmin(filtered)
  }, [options, searchText, enabledFilter, targetFilter])

  const refreshAll = async () => {
    await loadNodes({ keepSelection: true })
    await loadCustomIcons()
    if (selectedCapability && supportsProvider) {
      await loadOptions(selectedCapability)
      await loadOverview(selectedCapability)
      await loadNodeModelBindings(selectedCapability)
    }
  }

  const patchOptionLocally = (updated: ProviderOptionAdminRow) => {
    setOptions((prev) => prev.map((r) => (r.id === updated.id ? updated : r)))
  }

  const onToggleEnabled = async (row: ProviderOptionAdminRow, enabled: boolean) => {
    setBusyId(row.id)
    try {
      const res = enabled ? await platformEnableProviderOption(row.id) : await platformDisableProviderOption(row.id)
      if (res.data.enabled !== enabled) {
        if (selectedCapability) await loadOptions(selectedCapability)
        message.error('节点启用状态未持久化，请重试')
        return
      }
      patchOptionLocally(res.data)
      if (selectedCapability) await loadOptions(selectedCapability)
      message.success(enabled ? '已启用' : '已停用')
      await loadNodes({ keepSelection: true })
    } catch (err) {
      if (selectedCapability) await loadOptions(selectedCapability)
      message.error(errMsg(err, '状态更新失败'))
    } finally {
      setBusyId(null)
    }
  }

  const onSetDefault = async (row: ProviderOptionAdminRow) => {
    setBusyId(row.id)
    try {
      const res = await platformSetDefaultProviderOption(row.id)
      setOptions((prev) =>
        prev.map((r) => {
          if (r.id === res.data.id) return res.data
          return { ...r, is_default: false }
        }),
      )
      message.success(`已将「${res.data.frontend_display_name}」设为默认`)
      await loadNodes({ keepSelection: true })
    } catch (err) {
      message.error(errMsg(err, '设置默认失败'))
    } finally {
      setBusyId(null)
    }
  }

  const onWorkerOnline = async (row: ProviderOptionAdminRow) => {
    const workerId = row.mapping?.worker_id
    if (!workerId) return
    setBusyId(row.id)
    try {
      await platformSetProviderOptionWorkerOnline(workerId)
      message.success('已上线（复用 canonical Worker 控制）')
      if (selectedCapability) await loadOptions(selectedCapability)
    } catch (err) {
      message.error(errMsg(err, '上线失败'))
    } finally {
      setBusyId(null)
    }
  }

  const onWorkerOffline = async (row: ProviderOptionAdminRow) => {
    const workerId = row.mapping?.worker_id
    if (!workerId) return
    setBusyId(row.id)
    try {
      await platformSetProviderOptionWorkerOffline(workerId)
      message.success('已进入 draining（优雅下线，不强制杀进程）')
      if (selectedCapability) await loadOptions(selectedCapability)
    } catch (err) {
      message.error(errMsg(err, '下线失败'))
    } finally {
      setBusyId(null)
    }
  }

  // Item 八/十一 — API Provider Global Runtime Control. Reuses the SAME
  // ProviderDefinition.status toggle already shipped on the Provider
  // Registry page (`platformUpdateProviderDefinition`) — never a second,
  // competing enable/disable API. Never touches Credential/ProviderOption
  // binding; purely `status: 'active' | 'disabled'`.
  const onProviderOperationalToggle = async (row: ProviderOptionAdminRow, disable: boolean) => {
    const providerId = row.mapping?.provider_definition_id
    if (!providerId) return
    setBusyId(row.id)
    try {
      await platformUpdateProviderDefinition(providerId, { status: disable ? 'disabled' : 'active' })
      message.success(disable ? '该 Provider 已全局停用（Credential/绑定保留）' : '该 Provider 已恢复路由')
      if (selectedCapability) await loadOptions(selectedCapability)
    } catch (err) {
      message.error(errMsg(err, '操作失败'))
    } finally {
      setBusyId(null)
    }
  }

  const onSaveDisplayName = async (row: ProviderOptionAdminRow, next: string) => {
    setBusyId(row.id)
    try {
      const res = await platformUpdateProviderOptionDisplayName(row.id, next)
      patchOptionLocally(res.data)
      message.success('已保存（provider_option_id 保持不变）')
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
      throw err
    } finally {
      setBusyId(null)
    }
  }

  const onSaveDescription = async (row: ProviderOptionAdminRow, next: string) => {
    setBusyId(row.id)
    try {
      const res = await platformUpdateProviderOptionDescription(row.id, next || null)
      patchOptionLocally(res.data)
      message.success('已保存')
    } catch (err) {
      message.error(errMsg(err, '保存失败'))
      throw err
    } finally {
      setBusyId(null)
    }
  }

  // Task spec §30 — disposable/E2E fixture Provider Option cleanup.
  // Distinct from disable: no undo, and it also removes mapping history.
  const onDeleteOption = async (row: ProviderOptionAdminRow) => {
    setBusyId(row.id)
    try {
      await platformDeleteProviderOption(row.id)
      setOptions((prev) => prev.filter((r) => r.id !== row.id))
      message.success('已删除')
      await loadNodes({ keepSelection: true })
    } catch (err) {
      message.error(errMsg(err, '删除失败'))
    } finally {
      setBusyId(null)
    }
  }

  const onToggleNodeModelBindingEnabled = async (row: NodeModelBindingRow, enabled: boolean) => {
    setNodeModelBindingBusyId(row.id)
    try {
      const res = await platformSetNodeModelBindingEnabled(row.id, enabled)
      setNodeModelBindings((prev) => prev.map((r) => (r.id === res.data.id ? res.data : r)))
      message.success(enabled ? '已启用' : '已停用')
    } catch (err) {
      message.error(errMsg(err, '状态更新失败'))
    } finally {
      setNodeModelBindingBusyId(null)
    }
  }

  const onSetNodeModelBindingDefault = async (row: NodeModelBindingRow) => {
    setNodeModelBindingBusyId(row.id)
    try {
      const res = await platformSetNodeModelBindingDefault(row.id)
      setNodeModelBindings((prev) =>
        prev.map((r) => {
          if (r.id === res.data.id) return res.data
          return { ...r, is_default: false }
        }),
      )
      message.success('已设为默认模型')
    } catch (err) {
      message.error(errMsg(err, '设置默认失败'))
    } finally {
      setNodeModelBindingBusyId(null)
    }
  }

  const moveNodeModelBinding = async (row: NodeModelBindingRow, direction: -1 | 1) => {
    const ordered = [...nodeModelBindings].sort((a, b) => a.sort_order - b.sort_order)
    const idx = ordered.findIndex((r) => r.id === row.id)
    const swapWith = idx + direction
    if (idx < 0 || swapWith < 0 || swapWith >= ordered.length) return
    setNodeModelBindingBusyId(row.id)
    try {
      const a = ordered[idx]
      const b = ordered[swapWith]
      const [resA, resB] = await Promise.all([
        platformSetNodeModelBindingSortOrder(a.id, b.sort_order),
        platformSetNodeModelBindingSortOrder(b.id, a.sort_order),
      ])
      setNodeModelBindings((prev) =>
        prev.map((r) => {
          if (r.id === resA.data.id) return resA.data
          if (r.id === resB.data.id) return resB.data
          return r
        }),
      )
      message.success('顺序已更新')
    } catch (err) {
      message.error(errMsg(err, '调整顺序失败'))
    } finally {
      setNodeModelBindingBusyId(null)
    }
  }

  const onDeleteNodeModelBinding = async (row: NodeModelBindingRow) => {
    setNodeModelBindingBusyId(row.id)
    try {
      await platformDeleteNodeModelBinding(row.id)
      setNodeModelBindings((prev) => prev.filter((r) => r.id !== row.id))
      message.success('已移除该模型绑定')
    } catch (err) {
      message.error(errMsg(err, '移除失败'))
    } finally {
      setNodeModelBindingBusyId(null)
    }
  }

  const moveOption = async (row: ProviderOptionAdminRow, direction: -1 | 1) => {
    const ids = options.map((o) => o.id)
    const idx = ids.indexOf(row.id)
    const swapWith = idx + direction
    if (idx < 0 || swapWith < 0 || swapWith >= ids.length || !selectedCapability) return
    const next = [...ids]
    ;[next[idx], next[swapWith]] = [next[swapWith], next[idx]]
    setBusyId(row.id)
    try {
      const res = await platformReorderProviderOptions(selectedCapability, next)
      setOptions(res.data.options ?? [])
      message.success('顺序已更新')
    } catch (err) {
      message.error(errMsg(err, '调整顺序失败'))
    } finally {
      setBusyId(null)
    }
  }

  const openCreate = () => {
    setCreateOpen(true)
  }

  const openRemap = (row: ProviderOptionAdminRow) => {
    setRemapRow(row)
    const target = row.mapping?.execution_target_type || 'LOCAL_WORKER'
    setRemapTargetType(target)
    remapForm.setFieldsValue({
      execution_target_type: target,
      worker_id: row.mapping?.worker_id ?? undefined,
      provider_definition_id: row.mapping?.provider_definition_id ?? undefined,
      credential_id: row.mapping?.credential_id ?? undefined,
      adapter_key: row.mapping?.adapter_key ?? undefined,
    })
    const provider = targetProviders.find((p) => p.id === row.mapping?.provider_definition_id)
    if (provider) {
      void platformListProviderCredentials(provider.provider_key)
        .then((res) => setCredentials(toCredentialOptions(res.data.credentials ?? [])))
        .catch(() => setCredentials([]))
    } else {
      setCredentials([])
    }
    setRemapOpen(true)
  }

  const onProviderPicked = async (providerDefinitionId: string, form: typeof remapForm) => {
    const provider = targetProviders.find((p) => p.id === providerDefinitionId)
    // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_RESOLUTION_
    // P0_V1 — server-resolved adapter_key when unambiguous; otherwise
    // left blank for the admin to pick among this Provider's OWN
    // scoped variants only (never another vendor's key, spec §9).
    form.setFieldsValue({
      credential_id: undefined,
      adapter_key: provider?.auto_resolved ? provider.adapter_key || undefined : undefined,
    })
    if (!provider) {
      setCredentials([])
      return
    }
    try {
      const res = await platformListProviderCredentials(provider.provider_key)
      setCredentials(toCredentialOptions(res.data.credentials ?? []))
    } catch {
      setCredentials([])
    }
  }

  const buildMappingFromForm = (values: Record<string, unknown>): ProviderOptionMappingInput => {
    const executionTargetType = String(values.execution_target_type)
    const priorConfig = (remapRow?.mapping?.config as Record<string, unknown> | null) ?? null

    // VistoeraTASK-00640 — align remap with AddProviderDrawer: AUTO clears
    // worker_id and sets ANY_HEALTHY; PINNED keeps worker_id + PINNED_RUNTIME.
    // Preserve adapter_key so unpinned ChatGPT Web LOCAL_WORKER AUTO still
    // resolves harness provider without a second tenant-visible Provider.
    if (executionTargetType === 'LOCAL_WORKER') {
      const dispatch = (values.worker_dispatch_mode as string) || WORKER_DISPATCH_PINNED
      const requiredCap =
        (values.required_worker_capability as string | undefined)?.trim() ||
        (typeof priorConfig?.required_worker_capability === 'string'
          ? priorConfig.required_worker_capability
          : selectedCapability) ||
        undefined
      const config: Record<string, unknown> = {
        ...(priorConfig || {}),
        dispatch_policy:
          dispatch === WORKER_DISPATCH_AUTO ? 'ANY_HEALTHY_COMPATIBLE_RUNTIME' : 'PINNED_RUNTIME',
      }
      if (requiredCap) config.required_worker_capability = requiredCap
      return {
        execution_target_type: 'LOCAL_WORKER',
        worker_id: dispatch === WORKER_DISPATCH_PINNED ? (values.worker_id as string | undefined) || null : null,
        provider_definition_id: null,
        credential_id: null,
        adapter_key: (values.adapter_key as string | undefined) || remapRow?.mapping?.adapter_key || null,
        provider_model_id: null,
        config,
      }
    }

    return {
      execution_target_type: executionTargetType,
      worker_id: (values.worker_id as string | undefined) || null,
      provider_definition_id: (values.provider_definition_id as string | undefined) || null,
      credential_id: (values.credential_id as string | undefined) || null,
      adapter_key: (values.adapter_key as string | undefined) || null,
      // TASK-00183 forensic finding — this form has no Model field, so
      // remap must round-trip the CURRENT mapping's provider_model_id/
      // config verbatim instead of implicitly clearing them. Before this
      // fix, EVERY remap (even of an already-healthy FIXED_MODEL Option
      // like Seedance) silently wiped its model binding + config on
      // confirm — a real, live data-corrupting bug, not merely cosmetic.
      provider_model_id:
        (values.provider_definition_id as string | undefined) === remapRow?.mapping?.provider_definition_id
          ? remapRow?.mapping?.provider_model_id ?? null
          : null,
      config: priorConfig,
    }
  }

  const submitRemap = async () => {
    if (!remapRow) return
    const values = await remapForm.validateFields()
    setRemapping(true)
    try {
      const res = await platformRemapProviderOption(remapRow.id, buildMappingFromForm(values))
      patchOptionLocally(res.data)
      message.success(`已重映射（mapping revision ${res.data.mapping_revision}）`)
      setRemapOpen(false)
      await loadNodes({ keepSelection: true })
    } catch (err) {
      message.error(errMsg(err, '重映射失败'))
    } finally {
      setRemapping(false)
    }
  }

  // TASK-00183 — canonical Model Binding repair Dialog. Opens for both
  // a genuinely broken FIXED_MODEL binding (needs_model_repair) AND a
  // RUNTIME_SELECTION_REQUIRED pooled Option (task spec §11: "点击修复
  // 模型绑定应打开候选模型 Dialog" applies to both — the button label
  // below distinguishes "修复" vs "更换" by current status only).
  const openModelBindingRepair = async (row: ProviderOptionAdminRow) => {
    setModelBindingRow(row)
    setModelBindingOpen(true)
    setModelBindingLoading(true)
    setModelBindingCandidates([])
    setModelBindingSelected(null)
    setModelBindingSafeMessage(null)
    try {
      const res = await platformGetModelBindingCandidates(row.id)
      setModelBindingCandidates(res.data.candidates)
      setModelBindingSafeMessage(res.data.model_binding_safe_message)
      setModelBindingSelected(res.data.current_provider_model_id || null)
    } catch (err) {
      message.error(errMsg(err, '加载候选模型失败'))
    } finally {
      setModelBindingLoading(false)
    }
  }

  const submitModelBindingRepair = async () => {
    if (!modelBindingRow || !modelBindingSelected) return
    setModelBindingSubmitting(true)
    try {
      const res = await platformRepairModelBinding(modelBindingRow.id, modelBindingSelected)
      patchOptionLocally(res.data)
      message.success('模型绑定已更新')
      setModelBindingOpen(false)
      await loadNodes({ keepSelection: true })
    } catch (err) {
      message.error(errMsg(err, '模型绑定更新失败'))
    } finally {
      setModelBindingSubmitting(false)
    }
  }

  const mappingFields = (
    form: typeof remapForm,
    targetType: string,
    setTargetType: (v: string) => void,
    workerDispatchMode: string | undefined,
  ) => (
    <>
      <Form.Item
        name="execution_target_type"
        label="执行目标类型"
        rules={[{ required: true, message: '请选择执行目标类型' }]}
      >
        <Select
          options={[
            ...EXECUTION_PLAN_CREATE_TARGET_TYPES.map(({ value, label }) => ({ value, label })),
            ...(targetType === 'BROWSER_PROVIDER' ? [{ value: 'BROWSER_PROVIDER', label: 'Worker（历史）' }] : []),
          ]}
          onChange={(v) => {
            setTargetType(v)
            form.setFieldsValue({
              worker_id: undefined,
              provider_definition_id: undefined,
              credential_id: undefined,
            })
            setCredentials([])
          }}
        />
      </Form.Item>
      {isWorkerExecutionTarget(targetType) && targetType !== 'BROWSER_PROVIDER' && (
        <>
          <Form.Item name="worker_dispatch_mode" label="Worker 调度方式" initialValue={WORKER_DISPATCH_PINNED}>
            <Select
              options={[
                { value: WORKER_DISPATCH_PINNED, label: '固定 Worker' },
                { value: WORKER_DISPATCH_AUTO, label: '自动调度' },
              ]}
            />
          </Form.Item>
          <Form.Item
            name="worker_id"
            label="Worker Runtime"
            rules={
              workerDispatchMode === WORKER_DISPATCH_AUTO ? [] : [{ required: true, message: '请选择 Worker' }]
            }
            extra={workerRuntimesError || undefined}
          >
            <Select
              showSearch
              optionFilterProp="label"
              allowClear={workerDispatchMode === WORKER_DISPATCH_AUTO}
              options={workerRuntimes.map((w) => ({
                value: w.worker_id,
                label: formatWorkerRuntimeOptionLabel(w),
              }))}
              placeholder={workerRuntimesError || '选择已注册的 Worker Runtime'}
            />
          </Form.Item>
          <Form.Item name="required_worker_capability" label="执行能力">
            <Input placeholder={selectedCapability || 'canonical capability id'} />
          </Form.Item>
        </>
      )}
      {targetType === 'BROWSER_PROVIDER' && (
        <Typography.Paragraph type="secondary" style={{ fontSize: 12 }}>
          历史 Browser Provider 方案：请通过 Worker 类型重新映射以绑定 Worker Registry（只读兼容展示）。
        </Typography.Paragraph>
      )}
      {isExternalApiExecutionTarget(targetType) && (
        <>
          <Form.Item
            name="provider_definition_id"
            label="Provider Definition"
            rules={[{ required: true, message: '请选择 Provider' }]}
          >
            <Select
              showSearch
              optionFilterProp="label"
              options={targetProviders.map((p) => ({
                value: p.id,
                label: `${p.display_name} (${p.provider_key})`,
              }))}
              onChange={(id) => void onProviderPicked(id, form)}
              placeholder={targetProviders.length ? '选择兼容该 Capability 的 Provider' : '当前无兼容 Provider'}
            />
          </Form.Item>
          {targetType === 'EXTERNAL_API' && (
            <Form.Item name="credential_id" label="凭证（Credential Center）">
              <Select
                allowClear
                showSearch
                optionFilterProp="label"
                options={credentials.map((c) => ({
                  value: c.id,
                  label: `${c.name} · ${c.status}${c.health_status ? ` · ${c.health_status}` : ''}`,
                }))}
                placeholder="选择已登记凭证（不展示明文密钥）"
              />
            </Form.Item>
          )}
          {(() => {
            // VISTOERA_FUNCTION_NODE_PROVIDER_OPTION_ADAPTER_AUTO_
            // RESOLUTION_P0_V1 (spec §9/§15 hard rule) — never a
            // freeform Adapter Key text box (that let an admin type
            // e.g. "fashn" for a DeepSeek mapping). ProviderDefinition
            // resolves its own adapter; backend re-validates/overrides
            // regardless of what this form sends (defense in depth).
            const provider = targetProviders.find((p) => p.id === remapProviderId)
            if (!provider) return null
            if (provider.auto_resolved) {
              return (
                <div
                  style={{
                    marginBottom: 16,
                    padding: '10px 12px',
                    borderRadius: 8,
                    background: platformColors.primarySoft,
                    border: `1px solid ${platformColors.border}`,
                  }}
                >
                  <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block' }}>
                    执行方式
                  </Typography.Text>
                  <Typography.Text strong>{provider.adapter_display_label || provider.display_name}</Typography.Text>
                  <Tag color="blue" style={{ marginLeft: 8 }}>
                    自动
                  </Tag>
                </div>
              )
            }
            const options = (provider.selectable_adapters || []).map((a) => ({ value: a.key, label: a.display_name }))
            return (
              <Form.Item
                name="adapter_key"
                label="Adapter"
                rules={[{ required: true, message: '请选择 Adapter' }]}
                extra="仅显示该厂商自身的可选变体，不会显示其他厂商的 Adapter。"
              >
                <Select options={options} placeholder="选择该厂商的执行变体" />
              </Form.Item>
            )
          })()}
        </>
      )}
    </>
  )

  const columns: ColumnsType<ProviderOptionAdminRow> = [
    {
      title: t('providers.overview.columns.displayName'),
      dataIndex: 'frontend_display_name',
      key: 'frontend_display_name',
      width: COL_WIDTH.displayName,
      ellipsis: true,
      onCell: () => ({ className: 'provider-option-name-cell' }),
      render: (name: string, row) => (
        <Space size={6} style={{ maxWidth: '100%' }} wrap={false}>
          <span style={{ minWidth: 0, flex: 1 }}>
            <span data-testid="provider-option-scheme-primary-name">
              <InlineEditText
                value={name}
                disabled={busyId === row.id}
                textClassName="provider-option-name-text"
                onSave={(next) => onSaveDisplayName(row, next)}
              />
            </span>
          </span>
          {row.is_default && <Tag color="blue">{t('providers.overview.defaultTag')}</Tag>}
        </Space>
      ),
    },
    {
      title: t('providers.overview.columns.description'),
      dataIndex: 'frontend_description',
      key: 'frontend_description',
      // No fixed width: this is the primary flexible column. minWidth via
      // onCell prevents Ant Design scroll.x + fixed siblings from crushing it.
      onHeaderCell: () => ({ style: { minWidth: COL_WIDTH.description } }),
      onCell: () => ({
        className: 'provider-option-desc-cell',
        style: { minWidth: COL_WIDTH.description },
      }),
      render: (v: string | null, row) => (
        <div data-testid="provider-option-description-cell">
          <InlineEditText
            value={v || ''}
            placeholder={t('providers.overview.addDescription')}
            multiline
            disabled={busyId === row.id}
            textClassName="provider-option-desc-text"
            onSave={(next) => onSaveDescription(row, next)}
          />
        </div>
      ),
    },
    {
      title: t('providers.overview.columns.provider'),
      key: 'provider',
      width: COL_WIDTH.provider,
      onCell: () => ({ className: 'provider-option-vendor-cell' }),
      render: (_, row) => {
        const label = providerLabel(row)
        return (
          <Tooltip title={label === '—' ? undefined : label} placement="topLeft">
            <span className="provider-option-vendor-text" data-testid="provider-option-provider-cell">
              {label}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: t('providers.overview.columns.model'),
      key: 'model',
      width: COL_WIDTH.model,
      onCell: () => ({ className: 'provider-option-vendor-cell' }),
      render: (_, row) => {
        const status = row.mapping?.model_binding_status
        const label = row.mapping?.needs_model_repair
          ? t('providers.drawer.modelNeedsRepair')
          : status === 'RUNTIME_SELECTION_REQUIRED'
            ? t('providers.drawer.modelSelectionRequired')
            : modelLabel(row)
        const color = row.mapping?.needs_model_repair
          ? '#cf1322'
          : status === 'RUNTIME_SELECTION_REQUIRED'
            ? '#d48806'
            : undefined
        return (
          <Tooltip title={row.mapping?.model_binding_safe_message || (label === '—' ? undefined : label)} placement="topLeft">
            <span
              className="provider-option-vendor-text"
              data-testid="provider-option-model-cell"
              style={color ? { color } : undefined}
            >
              {label}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: t('providers.overview.usage30d'),
      key: 'usage_30d',
      width: COL_WIDTH.usage,
      render: (_, row) => {
        if (row.usage_30d != null) {
          return <span data-testid="provider-option-usage-30d">{String(row.usage_30d)}</span>
        }
        const count = overview?.per_option?.find((p) => p.provider_option_id === row.id)?.call_count
        return count != null ? String(count) : '0'
      },
    },
    {
      title: '可用 Runtime',
      key: 'runtimeAvailability',
      width: 90,
      render: (_, row) => {
        const avail = row.mapping?.worker_runtime_availability
        if (!avail) return '—'
        if (avail.status === 'unavailable') {
          return (
            <Tooltip title="Worker 状态暂不可用">
              <span className="provider-option-vendor-text">—</span>
            </Tooltip>
          )
        }
        const n = avail.ready_runtimes
        if (n == null) return '—'
        return <span data-testid="provider-option-runtime-count">{n}</span>
      },
    },
    {
      title: t('providers.overview.columns.type'),
      key: 'type',
      width: COL_WIDTH.type,
      render: (_, row) => {
        const tt = row.mapping?.execution_target_type || ''
        const label = TARGET_TYPE_LABEL[tt] || tt || '—'
        if (!tt) return '—'
        return <Tag color={TARGET_TYPE_TAG_COLOR[tt] || 'default'}>{label}</Tag>
      },
    },
    {
      title: t('providers.overview.columns.status'),
      key: 'status',
      width: COL_WIDTH.status,
      render: (_, row) => statusCell(row),
    },
    {
      title: t('providers.overview.columns.enabled'),
      dataIndex: 'enabled',
      key: 'enabled',
      width: COL_WIDTH.enabled,
      render: (enabled: boolean, row) => (
        <Switch checked={enabled} loading={busyId === row.id} onChange={(next) => void onToggleEnabled(row, next)} />
      ),
    },
    {
      title: t('providers.overview.columns.default'),
      key: 'default',
      width: COL_WIDTH.default,
      render: (_, row) =>
        row.is_default ? (
          <Tag color="blue">{t('providers.overview.currentDefault')}</Tag>
        ) : (
          <Button size="small" disabled={!row.enabled || busyId === row.id} onClick={() => void onSetDefault(row)}>
            {t('providers.overview.setDefault')}
          </Button>
        ),
    },
    {
      title: '执行能力',
      key: 'requiredCapability',
      width: COL_WIDTH.executionTarget,
      render: (_, row) => {
        const cap = row.mapping?.required_worker_capability
        return (
          <Tooltip title={cap || undefined}>
            <span className="provider-option-vendor-text" data-testid="provider-option-capability-cell">
              {cap || '—'}
            </span>
          </Tooltip>
        )
      },
    },
    {
      title: t('providers.overview.columns.lifecycle'),
      key: 'lifecycle',
      width: COL_WIDTH.lifecycle,
      render: (_, row) => {
        const m = row.mapping
        // Item 九 — Worker-backed Runtime Control (LOCAL_WORKER AND
        // BROWSER_PROVIDER with a pinned worker_id both reuse the exact
        // same canonical `set_local_worker_online/offline` -> `Worker
        // RegistryService.set_control_state` call the Worker 节点 page's
        // own Enable/Drain buttons use — never a second lifecycle API).
        if ((m?.execution_target_type === 'LOCAL_WORKER' || m?.execution_target_type === 'BROWSER_PROVIDER') && m.worker_id) {
          return (
            <Space size={4} wrap={false}>
              <Button size="small" disabled={busyId === row.id} onClick={() => void onWorkerOnline(row)}>
                {t('providers.overview.online')}
              </Button>
              <Popconfirm
                title="将 Worker 进入 draining（优雅下线）？"
                description="不会强制杀进程；进行中的任务会尽量完成。Worker 节点页面会同步看到相同状态。"
                onConfirm={() => void onWorkerOffline(row)}
              >
                <Button size="small" danger disabled={busyId === row.id}>
                  {t('providers.overview.offline')}
                </Button>
              </Popconfirm>
            </Space>
          )
        }
        // Item 八/十一 — API Provider Global Runtime Control (no Worker
        // process; toggles `ProviderDefinition.status`, never touches
        // Credential/binding).
        if (m?.execution_target_type === 'EXTERNAL_API' && m.provider_definition_id) {
          const disabled = m.provider_operational_status === 'disabled'
          return disabled ? (
            <Button size="small" disabled={busyId === row.id} onClick={() => void onProviderOperationalToggle(row, false)}>
              恢复路由
            </Button>
          ) : (
            <Popconfirm
              title="全局停用该 Provider？"
              description="Credential / ProviderOption 绑定均保留；仅阻止新的 execution；已在运行的任务按现有 lifecycle 完成。"
              onConfirm={() => void onProviderOperationalToggle(row, true)}
            >
              <Button size="small" danger disabled={busyId === row.id}>
                全局停用
              </Button>
            </Popconfirm>
          )
        }
        return <span style={{ color: platformColors.mutedText }}>—</span>
      },
    },
    {
      title: t('providers.overview.columns.actions'),
      key: 'actions',
      width: COL_WIDTH.actions,
      fixed: 'right',
      render: (_, row) => (
        <Space size={4} wrap={false} className="provider-option-actions">
          {row.mapping?.execution_target_type === 'EXTERNAL_API' && row.mapping?.provider_definition_id ? (
            <Button
              size="small"
              danger={Boolean(row.mapping?.needs_model_repair)}
              onClick={() => void openModelBindingRepair(row)}
            >
              {row.mapping?.needs_model_repair || row.mapping?.model_binding_status === 'RUNTIME_SELECTION_REQUIRED'
                ? t('providers.overview.repairModelBinding')
                : t('providers.overview.changeModel')}
            </Button>
          ) : null}
          <Button
            size="small"
            icon={<EyeOutlined />}
            data-testid="provider-option-view-detail"
            onClick={() => {
              setDetailOptionId(row.id)
              setDetailOpen(true)
            }}
          >
            查看详情
          </Button>
          <Button size="small" onClick={() => openRemap(row)}>
            {t('providers.overview.remap')}
          </Button>
          <Tooltip title={t('providers.overview.moveUp')}>
            <Button
              size="small"
              icon={<ArrowUpOutlined />}
              disabled={busyId === row.id}
              onClick={() => void moveOption(row, -1)}
            />
          </Tooltip>
          <Tooltip title={t('providers.overview.moveDown')}>
            <Button
              size="small"
              icon={<ArrowDownOutlined />}
              disabled={busyId === row.id}
              onClick={() => void moveOption(row, 1)}
            />
          </Tooltip>
          <Popconfirm
            title="删除该执行方案？"
            description="不可撤销，会一并删除历史 mapping revision。仅用于清理 E2E disposable/测试行或废弃配置。"
            onConfirm={() => void onDeleteOption(row)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} disabled={busyId === row.id} />
          </Popconfirm>
        </Space>
      ),
    },
  ]

  return (
    <div data-testid="provider-management-page">
      <div
        style={{
          marginBottom: 8,
          display: 'flex',
          justifyContent: 'space-between',
          gap: 12,
          alignItems: 'flex-start',
        }}
      >
        <div>
          <Typography.Title level={4} style={{ margin: 0 }} data-testid="function-node-management-title">
            {t('providers.pageTitle')}
          </Typography.Title>
          <Typography.Text type="secondary">{t('providers.pageSubtitle')}</Typography.Text>
        </div>
        <Space>
          <Button icon={<ReloadOutlined />} onClick={() => void refreshAll()} data-testid="function-node-refresh-button">
            {t('providers.refresh')}
          </Button>
        </Space>
      </div>

      {nodesError && <Alert type="error" showIcon message={nodesError} style={{ marginBottom: 12 }} />}

      <div className="provider-node-layout" style={{ display: 'flex', gap: 12, alignItems: 'stretch', minHeight: 560 }}>
        <div
          data-testid="provider-node-rail"
          className="pf-scroll-panel provider-node-rail-panel"
          style={{
            width: 84,
            flexShrink: 0,
            background: platformColors.surface,
            border: `1px solid ${platformColors.border}`,
            borderRadius: 10,
            padding: '10px 0',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 6,
            overflowY: 'auto',
          }}
        >
          {nodesLoading && catalog.length === 0 && (
            <Typography.Text type="secondary" style={{ fontSize: 11 }}>
              …
            </Typography.Text>
          )}
          {catalog.map((entry, index) => {
            const selected = entry.node_type === selectedNodeType
            const online = entry.availability_status === 'ONLINE' && entry.is_enabled !== false
            const color = catalogEntryColor(entry)
            const icon = catalogEntryIcon(entry, iconOverrides, customIconsByRef)
            const capRow = entry.capability ? capabilityByKey[entry.capability] : null
            return (
              <div
                key={entry.node_type}
                data-catalog-index={index}
                style={{
                  position: 'relative',
                  opacity: dragFromIndex === index ? 0.45 : 1,
                  outline:
                    dragOverIndex === index && dragFromIndex !== null && dragOverIndex !== dragFromIndex
                      ? `1px dashed ${platformColors.primary}`
                      : undefined,
                }}
              >
                <Tooltip
                  placement="right"
                  title={
                    <div>
                      <div style={{ fontWeight: 600 }}>{entry.display_name}</div>
                      <div style={{ opacity: 0.85 }}>{entry.node_type}</div>
                      {entry.capability && <div style={{ opacity: 0.7, fontSize: 11 }}>{entry.capability}</div>}
                      <div style={{ opacity: 0.7, fontSize: 11 }}>
                        {online ? 'ONLINE' : 'OFFLINE'}
                        {entry.implementation_status === 'NOT_READY' ? ' · NOT_READY' : ''}
                      </div>
                      {capRow?.warning && <div style={{ color: '#fbbf24' }}>无可用 Provider / 缺少默认</div>}
                    </div>
                  }
                >
                  <button
                    type="button"
                    data-testid={`provider-node-rail-item-${entry.node_type}`}
                    data-selected={selected ? 'true' : 'false'}
                    onClick={() => selectNodeType(entry.node_type)}
                    style={{
                      position: 'relative',
                      width: 52,
                      height: 52,
                      borderRadius: 10,
                      border: selected ? `2px solid ${platformColors.primary}` : `1px solid ${platformColors.border}`,
                      background: selected ? platformColors.primarySoft : 'transparent',
                      color,
                      cursor: 'pointer',
                      display: 'inline-flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: 18,
                      padding: 0,
                    }}
                  >
                    <span
                      draggable={false}
                      onMouseDown={(e) => startCatalogReorder(e, index)}
                      onClick={(e) => e.stopPropagation()}
                      title="拖动排序"
                      style={{
                        position: 'absolute',
                        left: 2,
                        top: 2,
                        fontSize: 10,
                        opacity: 0.45,
                        cursor: 'grab',
                        lineHeight: 1,
                        color: platformColors.mutedText,
                      }}
                    >
                      <HolderOutlined />
                    </span>
                    {icon}
                    <span
                      data-testid={`provider-node-rail-availability-${entry.node_type}`}
                      style={{
                        position: 'absolute',
                        bottom: 3,
                        right: 3,
                        width: 8,
                        height: 8,
                        borderRadius: '50%',
                        background: online ? '#22c55e' : '#94a3b8',
                        boxShadow: `0 0 0 1px ${platformColors.surface}`,
                      }}
                    />
                    {capRow?.warning && (
                      <WarningFilled
                        data-testid={`provider-node-rail-warning-${entry.node_type}`}
                        style={{
                          position: 'absolute',
                          top: -2,
                          right: -2,
                          fontSize: 12,
                          color: platformColors.warning,
                          background: platformColors.surface,
                          borderRadius: '50%',
                        }}
                      />
                    )}
                  </button>
                </Tooltip>
              </div>
            )
          })}
        </div>

        <div style={{ flex: 1, minWidth: 0 }}>
          {selectedEntry && (
            <div
              data-testid="provider-selected-node-header"
              className="provider-selected-node-header-sticky"
              style={{
                background: platformColors.surface,
                border: `1px solid ${platformColors.border}`,
                borderRadius: 10,
                padding: '12px 16px',
                marginBottom: 10,
                display: 'flex',
                gap: 14,
                alignItems: 'center',
              }}
            >
              <Tooltip title="点击修改节点图标（所有 Surface 自动同步）">
                <button
                  type="button"
                  data-testid="provider-node-header-icon-button"
                  onClick={() => setIconPickerOpen(true)}
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: 12,
                    background: platformColors.primarySoft,
                    color: selectedColor,
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontSize: 22,
                    flexShrink: 0,
                    border: 'none',
                    cursor: 'pointer',
                  }}
                >
                  {selectedIcon}
                </button>
              </Tooltip>
              <div style={{ minWidth: 0, flex: 1 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Typography.Title level={5} style={{ margin: 0 }}>
                    {selectedEntry.display_name}
                  </Typography.Title>
                  <Typography.Text type="secondary">{selectedEntry.node_type}</Typography.Text>
                  {selectedEntry.capability && <Tag>{selectedEntry.capability}</Tag>}
                  {selectedEntry.implementation_status === 'NOT_READY' && <Tag color="orange">NOT_READY</Tag>}
                  {selectedEntry.availability_status === 'ONLINE' ? (
                    <Popconfirm
                      title="确认下线此节点？"
                      description="下线后，租户将无法新建或再次生成此节点。历史 Canvas 中已有节点仍保留并可查看。"
                      onConfirm={() => void onToggleAvailability(false)}
                    >
                      <Switch
                        checked
                        loading={availabilityBusy}
                        checkedChildren="ONLINE"
                        unCheckedChildren="OFFLINE"
                        data-testid="provider-node-availability-switch"
                      />
                    </Popconfirm>
                  ) : (
                    <Switch
                      checked={false}
                      loading={availabilityBusy}
                      checkedChildren="ONLINE"
                      unCheckedChildren="OFFLINE"
                      data-testid="provider-node-availability-switch"
                      onChange={(next) => {
                        if (next) void onToggleAvailability(true)
                      }}
                    />
                  )}
                </div>
                <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                  {selectedEntry.description ||
                    selectedMeta?.description ||
                    (supportsProvider
                      ? '该节点支持可选择的执行方案（Execution Option）架构。'
                      : '该节点无执行方案（Asset / tooling）。')}
                  {supportsProvider && selectedCapabilityRow && (
                    <>
                      {' · '}
                      {selectedCapabilityRow.enabled_option_count}/{selectedCapabilityRow.option_count} 启用
                      {selectedCapabilityRow.has_default ? ' · 已设默认' : ' · 未设默认'}
                    </>
                  )}
                </Typography.Text>
              </div>
            </div>
          )}

          {selectedEntry?.node_type && (
            <Tabs
              activeKey={activeTab}
              onChange={setActiveTab}
              data-testid="provider-node-content-tabs"
              items={[
                {
                  key: 'overview',
                  label: t('providers.tabs.overview'),
                  children: (
                    <>
                      {supportsProvider && (
                        <div
                          data-testid="provider-overview-cards"
                          style={{
                            display: 'grid',
                            gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
                            gap: 10,
                            marginBottom: 12,
                          }}
                        >
                          {[
                            {
                              key: 'usage',
                              label: t('providers.overview.usage30d'),
                              value:
                                overviewLoading
                                  ? '…'
                                  : overview?.total_calls != null
                                    ? String(overview.total_calls)
                                    : t('providers.overview.dash'),
                            },
                            {
                              key: 'enabled',
                              label: t('providers.overview.enabledCount'),
                              value:
                                overviewLoading
                                  ? '…'
                                  : overview?.enabled_count != null
                                    ? String(overview.enabled_count)
                                    : t('providers.overview.dash'),
                            },
                            {
                              key: 'default',
                              label: t('providers.overview.defaultProvider'),
                              value:
                                overviewLoading
                                  ? '…'
                                  : overview?.default_provider_name || t('providers.overview.dash'),
                            },
                            {
                              key: 'latency',
                              label: t('providers.overview.avgLatency'),
                              value:
                                overviewLoading
                                  ? '…'
                                  : overview?.avg_latency_ms != null
                                    ? t('providers.overview.ms', {
                                        value: Math.round(overview.avg_latency_ms),
                                      })
                                    : t('providers.overview.dash'),
                            },
                          ].map((card) => (
                            <div
                              key={card.key}
                              style={{
                                background: platformColors.surface,
                                border: `1px solid ${platformColors.border}`,
                                borderRadius: 10,
                                padding: '12px 14px',
                              }}
                            >
                              <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                {card.label}
                              </Typography.Text>
                              <div
                                className="provider-overview-card-value"
                                style={{ color: platformColors.primaryText }}
                                title={typeof card.value === 'string' ? card.value : undefined}
                              >
                                {card.value}
                              </div>
                            </div>
                          ))}
                        </div>
                      )}

                      {optionsError && (
                        <Alert type="error" showIcon message={optionsError} style={{ marginBottom: 10 }} />
                      )}

                      {supportsProvider && selectedCapability && (
                        <SectionCard
                          title="已绑定模型（Model Catalog）"
                          extra={
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              onClick={() => setAddModelOpen(true)}
                              data-testid="add-model-button"
                            >
                              添加模型
                            </Button>
                          }
                        >
                          <Typography.Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 10 }}>
                            从模型管理中已启用的 Provider Model 批量绑定到该节点。租户 Canvas 只会看到此处启用
                            且可见的模型，不会看到 Provider 凭证信息。
                          </Typography.Text>
                          <Table
                            size="small"
                            rowKey="id"
                            loading={nodeModelBindingsLoading}
                            dataSource={[...nodeModelBindings].sort((a, b) => a.sort_order - b.sort_order)}
                            pagination={false}
                            locale={{ emptyText: '尚未绑定任何模型，点击“添加模型”开始' }}
                            columns={[
                              {
                                title: '模型',
                                key: 'model',
                                render: (_, row) => (
                                  <Space size={6}>
                                    <span>{row.display_name_override || row.model_display_name || row.model_external_id}</span>
                                    {row.is_default && <Tag color="blue">默认</Tag>}
                                    {row.model_enabled === false && <Tag color="red">模型已停用</Tag>}
                                  </Space>
                                ),
                              },
                              {
                                title: 'Provider',
                                key: 'provider',
                                width: 160,
                                render: (_, row) => row.provider_display_name || row.provider_key || '—',
                              },
                              {
                                title: '外部 Model ID',
                                key: 'external_id',
                                width: 200,
                                render: (_, row) => (
                                  <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                                    {row.model_external_id || '—'}
                                  </Typography.Text>
                                ),
                              },
                              {
                                title: '启用',
                                key: 'enabled',
                                width: 72,
                                render: (_, row) => (
                                  <Switch
                                    checked={row.enabled}
                                    loading={nodeModelBindingBusyId === row.id}
                                    onChange={(next) => void onToggleNodeModelBindingEnabled(row, next)}
                                  />
                                ),
                              },
                              {
                                title: '默认',
                                key: 'default',
                                width: 100,
                                render: (_, row) =>
                                  row.is_default ? (
                                    <Tag color="blue">默认</Tag>
                                  ) : (
                                    <Button
                                      size="small"
                                      disabled={!row.enabled || nodeModelBindingBusyId === row.id}
                                      onClick={() => void onSetNodeModelBindingDefault(row)}
                                    >
                                      设为默认
                                    </Button>
                                  ),
                              },
                              {
                                title: '操作',
                                key: 'actions',
                                width: 130,
                                render: (_, row) => (
                                  <Space size={4}>
                                    <Tooltip title="上移">
                                      <Button
                                        size="small"
                                        icon={<ArrowUpOutlined />}
                                        disabled={nodeModelBindingBusyId === row.id}
                                        onClick={() => void moveNodeModelBinding(row, -1)}
                                      />
                                    </Tooltip>
                                    <Tooltip title="下移">
                                      <Button
                                        size="small"
                                        icon={<ArrowDownOutlined />}
                                        disabled={nodeModelBindingBusyId === row.id}
                                        onClick={() => void moveNodeModelBinding(row, 1)}
                                      />
                                    </Tooltip>
                                    <Popconfirm
                                      title="移除该模型绑定？"
                                      description="不会删除 ProviderModel 本身，仅移除该节点与此模型的绑定。"
                                      onConfirm={() => void onDeleteNodeModelBinding(row)}
                                    >
                                      <Button
                                        size="small"
                                        danger
                                        icon={<DeleteOutlined />}
                                        disabled={nodeModelBindingBusyId === row.id}
                                      />
                                    </Popconfirm>
                                  </Space>
                                ),
                              },
                            ]}
                          />
                        </SectionCard>
                      )}

                      {!supportsProvider ? (
                        <SectionCard title={t('providers.overview.optionSection')}>
                          <Typography.Text type="secondary" data-testid="provider-options-empty-asset">
                            {t('providers.overview.emptyAsset')}
                          </Typography.Text>
                        </SectionCard>
                      ) : (
                        <SectionCard
                          title={t('providers.overview.optionSection')}
                          extra={
                            <Button
                              type="primary"
                              icon={<PlusOutlined />}
                              disabled={!supportsProvider || !selectedCapability}
                              onClick={openCreate}
                              data-testid="provider-add-button"
                            >
                              {t('providers.addProvider')}
                            </Button>
                          }
                        >
                          <div
                            style={{
                              display: 'flex',
                              flexWrap: 'wrap',
                              gap: 8,
                              marginBottom: 10,
                              alignItems: 'center',
                            }}
                            data-testid="provider-options-filters"
                          >
                            {executionPlanSummary ? (
                              <Typography.Text type="secondary" style={{ fontSize: 12 }} data-testid="execution-plan-summary">
                                执行方案：{executionPlanSummary.total} · Worker：{executionPlanSummary.worker_backed} · API：
                                {executionPlanSummary.api_backed} · 可执行：{executionPlanSummary.executable}
                                {executionPlanSummary.default_display_name
                                  ? ` · 默认：${executionPlanSummary.default_display_name}`
                                  : ''}
                              </Typography.Text>
                            ) : null}
                            <Input.Search
                              allowClear
                              placeholder={t('providers.overview.searchPlaceholder')}
                              style={{ width: 260 }}
                              value={searchText}
                              onChange={(e) => setSearchText(e.target.value)}
                            />
                            <Select
                              allowClear
                              placeholder={t('providers.overview.filterEnabled')}
                              style={{ width: 120 }}
                              value={enabledFilter}
                              onChange={(v) => setEnabledFilter(v)}
                              options={[
                                { value: true, label: t('providers.overview.filterEnabledOn') },
                                { value: false, label: t('providers.overview.filterEnabledOff') },
                              ]}
                            />
                            <Select
                              allowClear
                              placeholder={t('providers.overview.filterTargetType')}
                              style={{ width: 140 }}
                              value={targetFilter}
                              onChange={(v) => setTargetFilter(v)}
                              options={Object.entries(TARGET_TYPE_LABEL).map(([value, label]) => ({
                                value,
                                label,
                              }))}
                            />
                          </div>
                          <div className="provider-options-table-wrap" data-testid="provider-options-table-wrap">
                            <Table
                              className="provider-options-table"
                              data-testid="provider-options-table"
                              size="small"
                              rowKey="id"
                              loading={optionsLoading}
                              columns={columns}
                              dataSource={filteredOptions}
                              pagination={false}
                              scroll={{ x: PROVIDER_OPTIONS_TABLE_MIN_WIDTH }}
                              locale={{
                                emptyText: selectedCapability
                                  ? t('providers.overview.emptyOptions')
                                  : t('providers.overview.selectNode'),
                              }}
                            />
                          </div>
                        </SectionCard>
                      )}
                    </>
                  ),
                },
                {
                  key: 'quickActions',
                  label: t('providers.tabs.quickActions'),
                  children: <NodeQuickActionsPanel sourceNodeType={selectedEntry.node_type} />,
                },
                {
                  key: 'skills',
                  label: t('providers.tabs.skills'),
                  children: (
                    <NodeSkillsPanel
                      capability={selectedCapability}
                      canvasNodeType={selectedEntry.node_type}
                    />
                  ),
                },
                {
                  key: 'financial',
                  label: t('providers.tabs.financial'),
                  children: (
                    <SectionCard title={t('providers.tabs.financial')}>
                      <Empty
                        data-testid="provider-financial-coming-soon"
                        description={
                          <div>
                            <div>{t('providers.financial.comingSoon')}</div>
                            <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                              {t('providers.financial.comingSoonHint')}
                            </Typography.Text>
                          </div>
                        }
                      />
                    </SectionCard>
                  ),
                },
              ]}
            />
          )}
        </div>
      </div>

      <NodeIconPicker
        open={iconPickerOpen}
        functionLabel={selectedEntry?.display_name || selectedMeta?.labelZh}
        functionNodeType={selectedNodeType}
        currentIconKey={selectedIconKey}
        confirmLoading={iconSaving}
        onCancel={() => setIconPickerOpen(false)}
        onSelect={(key) => void onPickIcon(key)}
      />

      {selectedCapability && (
        <AddProviderDrawer
          open={createOpen}
          capability={selectedCapability}
          nodeDisplayName={selectedEntry?.display_name || null}
          nodeType={selectedEntry?.node_type || selectedNodeType || null}
          targetWorkers={targetWorkers}
          targetProviders={targetProviders}
          onClose={() => setCreateOpen(false)}
          onCreated={() => void refreshAll()}
        />
      )}

      {selectedCapability && (
        <AddModelDrawer
          open={addModelOpen}
          capability={selectedCapability}
          nodeDisplayName={selectedEntry?.display_name || null}
          onClose={() => setAddModelOpen(false)}
          onAdded={() => void loadNodeModelBindings(selectedCapability)}
        />
      )}

      <Modal
        title={remapRow ? `重映射执行目标 · ${remapRow.frontend_display_name}` : '重映射执行目标'}
        open={remapOpen}
        onCancel={() => setRemapOpen(false)}
        onOk={() => void submitRemap()}
        confirmLoading={remapping}
        okText="写入新映射版本"
        width={560}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message="重映射会追加一条新的 mapping revision，不会破坏历史 run snapshot。provider_option_id 保持不变。"
        />
        <Form form={remapForm} layout="vertical">
          {mappingFields(remapForm, remapTargetType, setRemapTargetType, remapWorkerDispatchMode)}
        </Form>
      </Modal>

      <Modal
        title={
          modelBindingRow
            ? `${t('providers.drawer.modelBindingDialogTitle')} · ${modelBindingRow.frontend_display_name}`
            : t('providers.drawer.modelBindingDialogTitle')
        }
        open={modelBindingOpen}
        onCancel={() => setModelBindingOpen(false)}
        onOk={() => void submitModelBindingRepair()}
        confirmLoading={modelBindingSubmitting}
        okText={t('providers.drawer.modelBindingConfirm')}
        okButtonProps={{ disabled: !modelBindingSelected || modelBindingLoading }}
        width={560}
        destroyOnClose
      >
        <Alert
          type="info"
          showIcon
          style={{ marginBottom: 12 }}
          message={t('providers.drawer.modelBindingDialogHint')}
        />
        {modelBindingSafeMessage ? (
          <Alert type="warning" showIcon style={{ marginBottom: 12 }} message={modelBindingSafeMessage} />
        ) : null}
        {modelBindingLoading ? (
          <Typography.Text type="secondary">加载中…</Typography.Text>
        ) : modelBindingCandidates.length === 0 ? (
          <Empty description={t('providers.drawer.modelBindingNoCandidates')} />
        ) : (
          <Radio.Group
            value={modelBindingSelected}
            onChange={(e) => setModelBindingSelected(e.target.value)}
            style={{ width: '100%' }}
          >
            <Space direction="vertical" style={{ width: '100%' }}>
              {modelBindingCandidates.map((c) => (
                <Radio key={c.id} value={c.id} disabled={!c.enabled || !c.capability_compatible}>
                  <Space size={6}>
                    <span>{c.display_name}</span>
                    <Typography.Text type="secondary" style={{ fontSize: 12 }}>
                      {c.external_id}
                    </Typography.Text>
                    {c.id === modelBindingRow?.mapping?.provider_model_id ? (
                      <Tag color="blue">{t('providers.drawer.modelBindingCandidateCurrent')}</Tag>
                    ) : null}
                    {!c.capability_compatible ? (
                      <Tag color="red">{t('providers.drawer.modelBindingCandidateIncompatible')}</Tag>
                    ) : null}
                    {!c.enabled ? <Tag>{t('providers.drawer.modelBindingCandidateDisabled')}</Tag> : null}
                  </Space>
                </Radio>
              ))}
            </Space>
          </Radio.Group>
        )}
      </Modal>

      <ExecutionPlanDetailDrawer
        optionId={detailOptionId}
        open={detailOpen}
        onClose={() => {
          setDetailOpen(false)
          setDetailOptionId(null)
        }}
      />
    </div>
  )
}
